import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@repo/ui/components/ui/form";
import { AuthFlowCard } from "../auth-flow-card";
import z from "zod";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Input } from "@repo/ui/components/ui/input";
import { cn } from "@repo/common/utils/cn";
import { Button } from "@repo/ui/components/ui/button";
import { Body } from "@repo/ui/components/ui/text";
import { loginSchema, useLogin } from "./hooks/use-login";
import { useBiometricLogin } from "./hooks/use-biometric-login";
import { useForgotPassword } from "./hooks/use-forgot-password";
import { BiometricOptInDialog } from "./biometric-opt-in-dialog";
import { redirectToAppHome } from "@/lib/biometric-login";
import { InputGroup, InputGroupAddon, InputGroupInput } from "@repo/ui/components/ui/input-group";
import { Eye, EyeOff } from "lucide-react";
import { AUTH_FLOW_INPUT_CLASSNAME } from "../constants";
import { FormFooter } from "../form-footer";
import { useRouter } from "next/router";
const resetPasswordSchema = z.object({
  username: z.string().email("Please enter a valid email address")
});
export function Login({
  showForgotPassword: initialShowForgotPassword = false
}: {
  showForgotPassword?: boolean;
}) {
  const router = useRouter();
  const [resetSent, setResetSent] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(initialShowForgotPassword);
  const [showPassword, setShowPassword] = useState(false);
  const isResetSent = showForgotPassword && resetSent;
  useEffect(() => {
    if (resetSent) {
      const timer = setTimeout(() => {
        setResetSent(false);
        setShowForgotPassword(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [resetSent]);

  // stable resolver so validation isn't re-triggered by resolver identity changes
  const resolver = useMemo(() => zodResolver(showForgotPassword ? resetPasswordSchema : loginSchema), [showForgotPassword]);
  const form = useForm<z.infer<typeof loginSchema> | z.infer<typeof resetPasswordSchema>>({
    resolver,
    defaultValues: showForgotPassword ? {
      username: ""
    } : {
      username: "",
      password: ""
    }
  });
  const handleForgotPassword = useCallback(() => {
    // TODO JT - for now we're sending to php app until api is ready
    window.open(`${process.env.NEXT_PUBLIC_BASE_URL}/login/forget`, "_parent");
    // setShowForgotPassword(true);
  }, []);
  const offerOptInRef = useRef<(email: string, password: string) => Promise<void>>(async () => undefined);

  // Native availability is decided inside the adapter. A browser has no plugin,
  // so the password form stays as it is.
  const biometricEnabled = !showForgotPassword;
  const biometric = useBiometricLogin({
    enabled: biometricEnabled,
    onError: (message, email) => {
      if (email) {
        form.setValue("username", email);
      }
      form.setValue("password", "");
      form.setError("root", {
        type: "manual",
        message
      });
    },
    onPrefillEmail: email_0 => {
      if (!form.getValues("username")) {
        form.setValue("username", email_0);
      }
    }
  });
  offerOptInRef.current = biometric.offerOptIn;
  const handleLoginSuccess = useCallback(async (data: {
    accessToken?: string;
  }) => {
    if (!data?.accessToken) {
      return;
    }
    await offerOptInRef.current(String(form.getValues("username") ?? ""), String(form.getValues("password") ?? ""));
    redirectToAppHome();
  }, [form]);
  const handleUsePasswordInstead = useCallback(() => {
    biometric.usePasswordInstead();
    form.setFocus("password");
  }, [biometric, form]);
  const biometricSignInLabel = `Log in with ${biometric.label}`;
  const handleLoginError = useCallback((error: any) => {
    if (error?.statusCode === 401) {
      form.setError("root", {
        type: "manual",
        message: "Hmmm.... That doesn't seem to be the correct username and or password."
      });
    } else {
      form.setError("root", {
        type: "manual",
        message: "An error occurred. Please try again."
      });
    }
  }, [form]);
  const loginMutation = useLogin(handleLoginSuccess, handleLoginError);
  const forgotPasswordMutation = useForgotPassword();
  const handleSubmit = useCallback(async (data_0: z.infer<typeof loginSchema>) => {
    loginMutation.mutate(data_0);
  }, [loginMutation]);
  const handleResetPassword = useCallback(async (data_1: z.infer<typeof resetPasswordSchema>) => {
    forgotPasswordMutation.mutate(data_1);
    // clear the password field
    form.setValue("password", "");
    setResetSent(true);
  }, [forgotPasswordMutation, form]);
  const handleNavigateToSignup = useCallback(() => {
    router.push("/signup");
  }, [router]);
  return <div className="w-full flex flex-col gap-4 items-center justify-start md:justify-center">
      <AuthFlowCard title="Welcome back">
        <Form {...form}>
          <form onSubmit={showForgotPassword ? form.handleSubmit(handleResetPassword) : form.handleSubmit(handleSubmit as SubmitHandler<z.infer<typeof loginSchema> | z.infer<typeof resetPasswordSchema>>)} className="space-y-4 mx-auto w-full">
            {showForgotPassword ? <>
                {resetSent ? <div className="border bg-brand-surface border-status-success rounded-lg p-6 text-center">
                    <div className="mb-3">
                      <svg className="mx-auto" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                        <polyline points="22 4 12 14.01 9 11.01" />
                      </svg>
                    </div>
                    <h3 className="mb-2 text-status-success">
                      Check your email
                    </h3>
                    <p className="text-sm text-status-success">
                      We've sent password reset instructions to{" "}
                      <strong>{form.getValues("username")}</strong>
                    </p>
                  </div> : <FormField control={form.control} name={"username"} render={({
              field
            }) => <FormItem>
                        <FormLabel weight="medium">Email address</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="you@yourcompany.com" className={cn(form.formState.errors?.username ? "border-status-error" : "", AUTH_FLOW_INPUT_CLASSNAME)} sizeVariant="lg" data-testid="login-username" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />}
              </> : null}

            {!showForgotPassword ? <>
                {form.formState.errors.root && <FormMessage>
                    {form.formState.errors.root.message}
                  </FormMessage>}
                <FormField control={form.control} name={"username"} render={({
              field: field_0
            }) => <FormItem>
                      <FormLabel weight="medium">Email address</FormLabel>
                      <FormControl>
                        <Input {...field_0} placeholder="you@yourcompany.com" className={cn(form.formState.errors?.username ? "border-status-error" : "", AUTH_FLOW_INPUT_CLASSNAME)} sizeVariant="lg" data-testid="login-username" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />

                <FormField control={form.control} name={"password"} render={({
              field: field_1
            }) => <FormItem>
                      <FormLabel weight="medium">Password</FormLabel>
                      <FormControl>
                        <InputGroup className="h-12">
                          <InputGroupInput {...field_1} sizeVariant="lg" type={showPassword ? "text" : "password"} data-testid="login-password" />
                          <InputGroupAddon align="inline-end" onClick={() => setShowPassword(!showPassword)} className="cursor-pointer">
                            {showPassword ? <EyeOff /> : <Eye />}
                          </InputGroupAddon>
                        </InputGroup>
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />
              </> : null}

            {!showForgotPassword ? <div className="flex justify-end">
                <button type="button" onClick={handleForgotPassword} className="text-xs text-brand hover:underline">
                  Forgot password?
                </button>
              </div> : null}

            {!isResetSent ? <Button type="submit" data-testid="login-submit" className={cn("w-full py-3.5 bg-brand hover:bg-brand-alt active:bg-brand-alt text-surface h-12")} disabled={showForgotPassword ? form.formState.errors?.username || !form.getValues("username") : form.formState.errors?.["password"]}>
                {showForgotPassword ? "Send reset link" : "Log in"}
              </Button> : null}

            {!showForgotPassword && biometric.showSignIn ? <>
                <Button type="button" data-testid="biometric-login" className={cn("w-full py-3.5 bg-brand hover:bg-brand-alt active:bg-brand-alt text-surface h-12")} onClick={() => {
              void biometric.signIn();
            }}>
                  {biometricSignInLabel}
                </Button>
                <button type="button" data-testid="biometric-use-password" onClick={handleUsePasswordInstead} className="text-sm text-brand hover:underline text-center">
                  Use password instead
                </button>
              </> : null}

            {showForgotPassword ? <Body className="text-center pt-3 text-xs text-foreground-muted hover:underline cursor-pointer" onClick={() => setShowForgotPassword(false)}>
                Back to login
              </Body> : null}
          </form>
        </Form>
      </AuthFlowCard>

      <FormFooter onClick={handleNavigateToSignup} buttonText="Create account" />
      {biometric.optIn.open ? <BiometricOptInDialog open={biometric.optIn.open} label={biometric.optIn.label} pending={biometric.optIn.pending} onEnable={biometric.optIn.onEnable} onDecline={biometric.optIn.onDecline} /> : null}
    </div>;
}